// Editor.jsx - Monaco-based code editor shell
// - Connects Monaco editor to global store (code, fileName, p5Version)
// - Delegates AI inline completions and context menu actions to services/editor/aiEditorFeatures
// - Delegates p5.js IntelliSense (signature help, hover, completions, types) to services/editor/p5Intellisense
// If you change AI or p5 behavior, prefer editing those helper modules instead of this file.
import { useRef, useState, useEffect } from 'react'
import Editor from '@monaco-editor/react'
import { useStore } from '../store/useStore'
import FileTabs from './FileTabs'
import { showToast } from './ui/Toast'
import AiHelpModal from './ui/AiHelpModal'
import { registerP5Intellisense } from '../services/editor/p5Intellisense'
import { registerInlineAiCompletion, registerAiContextMenuActions } from '../services/editor/aiEditorFeatures'

export default function CodeEditor() {
  const { 
    code, 
    fileName,
    setCode,
    p5Version,
    editorSettings
  } = useStore()
  
  const editorRef = useRef(null)
  
  // AI Help Modal state
  const [aiHelpModal, setAiHelpModal] = useState({
    isOpen: false,
    title: '',
    content: '',
    code: ''
  })

  // Cleanup event listeners when component unmounts
  useEffect(() => {
    return () => {
      if (editorRef.current?._highlightErrorCleanup) {
        editorRef.current._highlightErrorCleanup()
      }
    }
  }, [])

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor

    // AI inline completions (GPT-5.1 Codex)
    registerInlineAiCompletion(monaco)

    // p5.js IntelliSense (signature help, hover, completions, types)
    registerP5Intellisense(monaco, p5Version)

    // AI context menu actions (Explain/Fix/Optimize)
    registerAiContextMenuActions(editor, {
      onShowHelpModal: ({ title, content, code }) => {
        setAiHelpModal({
          isOpen: true,
          title,
          content,
          code
        })
      },
      showToast
    })

    // Add Ctrl+Enter to run sketch
    editor.addAction({
      id: 'run-sketch',
      label: 'Run Sketch',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      run: () => {
        window.dispatchEvent(new CustomEvent('runSketch'))
      }
    })

    // Add Ctrl+S to save/sync
    editor.addAction({
      id: 'save-sketch',
      label: 'Sync to p5.js',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run: () => {
        window.dispatchEvent(new CustomEvent('syncSketch'))
      }
    })

    // Add Ctrl+F to open find widget (Monaco has this built-in but let's ensure it's enabled)
    editor.addAction({
      id: 'find',
      label: 'Find',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF],
      run: () => {
        editor.getAction('actions.find').run()
      }
    })

    // Add Ctrl+H for find and replace
    editor.addAction({
      id: 'replace',
      label: 'Replace',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH],
      run: () => {
        editor.getAction('editor.action.startFindReplaceAction').run()
      }
    })

    // Listen for error highlighting from console
    const handleHighlightError = (event) => {
      const { lineNumber, columnNumber } = event.detail
      if (lineNumber) {
        // Go to the line
        editor.revealLineInCenter(lineNumber)
        editor.setPosition({ 
          lineNumber: lineNumber, 
          column: columnNumber || 1 
        })
        
        // Add a decoration to highlight the error line
        const decorations = editor.deltaDecorations([], [
          {
            range: new monaco.Range(lineNumber, 1, lineNumber, 1000),
            options: {
              isWholeLine: true,
              className: 'error-highlight-line',
              glyphMarginClassName: 'error-glyph'
            }
          }
        ])
        
        // Remove the decoration after 3 seconds
        setTimeout(() => {
          editor.deltaDecorations(decorations, [])
        }, 3000)
      }
    }
    
    window.addEventListener('highlightError', handleHighlightError)
    
    // Store the cleanup function
    editor._highlightErrorCleanup = () => {
      window.removeEventListener('highlightError', handleHighlightError)
    }
  }

  const getLanguage = (filename) => {
    if (filename.endsWith('.js')) return 'javascript'
    if (filename.endsWith('.html')) return 'html'
    if (filename.endsWith('.css')) return 'css'
    if (filename.endsWith('.json')) return 'json'
    if (filename.endsWith('.md')) return 'markdown'
    if (filename.endsWith('.xml')) return 'xml'
    if (filename.endsWith('.yaml') || filename.endsWith('.yml')) return 'yaml'
    return 'plaintext'
  }

  // Check file type
  const isImage = /\.(png|jpg|jpeg|gif|svg|bmp|ico|webp)$/i.test(fileName)
  const isAudio = /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(fileName)

  return (
    <>
      <div className="bg-[#1e1e1e] rounded-lg overflow-hidden flex flex-col h-full">
        {/* File Tabs */}
        <FileTabs />

        {/* Editor, Image Preview, or Audio Player */}
        <div className="flex-1">
          {isImage ? (
            // Image Preview
            <div className="h-full flex items-center justify-center p-8 bg-gray-900">
              <div className="text-center">
                {code.startsWith('data:image') ? (
                  <img 
                    src={code} 
                    alt={fileName}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-gray-400">
                    <p className="mb-2">Image Preview</p>
                    <p className="text-sm">{fileName}</p>
                    <p className="text-xs mt-4">Image content detected</p>
                  </div>
                )}
              </div>
            </div>
          ) : isAudio ? (
            // Audio Player
            <div className="h-full flex items-center justify-center p-8 bg-gray-900">
              <div className="text-center bg-gray-800 p-8 rounded-lg max-w-md w-full">
                <div className="text-p5-pink mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                </div>
                <p className="text-white font-medium mb-2">{fileName}</p>
                {code.startsWith('data:audio') ? (
                  <>
                    <audio 
                      controls 
                      className="w-full mt-4"
                      src={code}
                    >
                      Your browser does not support the audio element.
                    </audio>
                    <p className="text-gray-400 text-xs mt-4">
                      Use loadSound('{fileName}') in your p5.js sketch
                    </p>
                  </>
                ) : (
                  <div className="text-gray-400">
                    <p className="text-sm">Audio file loaded</p>
                    <p className="text-xs mt-2">Ready for p5.sound</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Code Editor
            <Editor
              height="100%"
              language={getLanguage(fileName)}
              value={code}
              onChange={(value) => setCode(value || '')}
              onMount={handleEditorDidMount}
              theme="vs-dark"
              options={{
                // Core settings
                fontSize: editorSettings.fontSize,
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                lineNumbers: 'on',
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                readOnly: isImage || isAudio,
                
                // User-configurable settings
                wordWrap: editorSettings.wordWrap ? 'on' : 'off',
                minimap: { enabled: editorSettings.minimap },
                bracketPairColorization: { enabled: editorSettings.bracketColors },
                guides: { 
                  indentation: editorSettings.indentGuides,
                  bracketPairs: editorSettings.indentGuides 
                },
                renderLineHighlight: editorSettings.lineHighlight ? 'all' : 'none',
                smoothScrolling: editorSettings.smoothScrolling,
                cursorSmoothCaretAnimation: editorSettings.smoothScrolling ? 'on' : 'off',
                mouseWheelZoom: editorSettings.mouseWheelZoom,
                stickyScroll: { enabled: editorSettings.stickyScroll },
                
                // Always-on nice features
                matchBrackets: 'always',
                links: true,
              }}
            />
          )}
        </div>
      </div>

      {/* AI Help Popup Modal */}
      <AiHelpModal
        isOpen={aiHelpModal.isOpen}
        onClose={() => setAiHelpModal({ ...aiHelpModal, isOpen: false })}
        title={aiHelpModal.title}
        content={aiHelpModal.content}
        code={aiHelpModal.code}
      />
    </>
  )
}
